// Interface para autenticação
export interface Auth {
  token: string;
  user: User;
}

// Interfaces principais
export interface User {
  id: number;
  name: string;
  email: string;
  azure_id?: string;
  email_verified_at?: string;
  funcao_usuario: string;
  cargo_usuario: string;
  setor_usuario_fk?: number;
  setor_logado_fk?: number;
  emails?: boolean; // Se o usuário quer receber emails de notificação
  created_at?: string;
  updated_at?: string;
  setor?: Setor;
  setor_logado?: Setor;
  setores?: (Setor & { 
    pivot?: { 
      usuario_fk: number; 
      setor_fk: number; 
      funcao: 'USUARIO' | 'GESTOR'; 
      active: boolean;
      created_at?: string;
      updated_at?: string;
    } 
  })[];
  avatar?: string; // URL do avatar do usuário
}

export interface Pais {
  id: number;
  name: string;
  created_at?: string;
  updated_at?: string;
  estados?: Estado[];
}

export interface Estado {
  id: number;
  name: string;
  pais_fk: number;
  created_at?: string;
  updated_at?: string;
  pais?: Pais;
  cidades?: Cidade[];
}

export interface Cidade {
  id: number;
  name: string;
  estado_fk: number;
  created_at?: string;
  updated_at?: string;
  estado?: Estado;
}

export interface Setor {
  id: number;
  nome_setor: string;
  chefe_setor_fk?: number;
  cidade_fk: number;
  setor_fk?: number;
  equipe?: boolean;
  active?: boolean;
  created_at?: string;
  updated_at?: string;
  chefe?: User;
  cidade?: Cidade;
  setor?: Setor;
  usuarios?: User[];
  usuariosAtivos?: User[];
  usuarios_ativos?: User[];
  processos?: Processo[];
}

/**
 * Helper function para verificar se um setor é uma equipe
 * Funciona tanto com boolean (true/false) quanto com valores numéricos (0/1)
 * que podem vir de diferentes bancos de dados
 */
export const isEquipe = (setor: Setor): boolean => {
  return !!setor.equipe;
}

/**
 * Helper function para obter o tipo de entidade (setor ou equipe)
 */
export const getSetorType = (setor: Setor): 'setor' | 'equipe' => {
  return isEquipe(setor) ? 'equipe' : 'setor';
}

/**
 * Helper function para obter o label do tipo de entidade
 */
export const getSetorTypeLabel = (setor: Setor): 'Setor' | 'Equipe' => {
  return isEquipe(setor) ? 'Equipe' : 'Setor';
}

export interface Processo {
  id: number;
  nome_processo: string;
  tipo?: string;
  prioridade_baixa: number;
  prioridade_media: number;
  prioridade_alta: number;
  prioridade_urgente: number;
  setor_processo_fk: number;
  descricao: string;
  instrucoes?: string;
  links?: Array<{link: string; titulo?: string}>;
  anexos?: Array<{
    anexo: string;
    nome_anexo: string;
    usuario_criador_fk?: number;
  }>;
  template_schema?: {
    template_id: string;
    template_nome: string;
    versao?: string;
    schema?: {
      type?: string;
      properties?: Record<string, { title?: string; type?: string; [key: string]: unknown }>;
      required?: string[];
      [key: string]: unknown;
    };
    uiSchema?: Record<string, unknown>;
    fluxoSimplificado?: {
      ativo: boolean;
      demandanteAutomatico?: boolean;
      tituloAutomatico?: boolean;
      prioridadePadrao?: 'Baixa' | 'Media' | 'Alta' | 'Urgente';
      diasEntregaPadrao?: number;
    };
  } | null;
  gestor_demandante?: boolean | number;
  gestor_demandado?: boolean | number;
  executivo_demandante?: boolean | number;
  executivo_demandado?: boolean | number;
  equipe?: boolean | number; // Pode ser aberto por equipe
  user_fk?: number; // ID do usuário que fez a última alteração
  usuario?: User; // Usuário que fez a última alteração
  created_at?: string;
  updated_at?: string;
  setor?: Setor;
  autorizadores?: (Autorizador & {
    pivot?: ProcessoAutorizacao;
  })[];
  autorizacoes?: ProcessoAutorizacao[];
  acessos?: ProcessoAcesso[];
  acessocount?: number;
}

export interface ProcessoAcesso {
  id: number;
  processo_fk: number;
  usuario_fk: number;
  usuario?: User;
  created_at: string;
  updated_at: string;
}

export interface Autorizador {
  id: number;
  nome: string;
  usuario_fk: number;
  active: boolean;
  usuario?: User;
  processos?: (Processo & {
    pivot?: ProcessoAutorizacao;
  })[];
  created_at?: string;
  updated_at?: string;
}

export interface ProcessoAutorizacao {
  processo_fk: number;
  autorizador_fk: number;
  ordem: number | null;
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface Anexo {
  id: number;
  anexo: string; // Path/URL do arquivo
  nome_anexo: string; // Nome original do arquivo
  demanda_fk: number;
  usuario_criador_fk?: number; // FK para o usuário que fez upload
  active: boolean;
  created_at: string;
  updated_at: string;
  // Relacionamentos (assumindo que serão incluídos pelo backend)
  usuario_criador?: User; // Usuário que fez upload do anexo
}

export interface Demanda {
  id?: number;
  criado_por_fk?: number;
  demandante_fk?: number;
  setor_fk?: number;
  setor_enviou_fk?: number; // setor que enviou/criou a demanda
  demandado_fk?: number;
  processo_fk?: number;
  relacionada_fk?: number;
  titulo: string;
  prioridade: 'Baixa' | 'Media' | 'Alta' | 'Urgente';
  status?: 'EMATRIBUICAO' | 'RECEBIDA' | 'ATRIBUIDA' | 'EMPROGRESSO' | 'AGUARDANDOAUTORIZACAO' | 'SOLUCIONADA' | 'FINALIZADA' | 'DEVOLVIDA' | 'ERRADA' | 'RECUSADA' | 'EXCLUIDA';
  descricao: string;
  previsao_entrega: string;
  sugestao_entrega: string;
  entregue?: string;
  created_at?: string;
  updated_at?: string;
  
  // Dados do template (quando a demanda foi criada com template)
  template_respostas?: {
    template_id: string;
    template_versao: string;
    preenchido_em: string;
    respostas: Record<string, unknown>;
  };
  
  // Relacionamentos
  criador?: User;
  criado_por?: User; // Alias para compatibilidade
  demandante?: User;
  demandado?: User;
  demandados?: User[]; // Múltiplos responsáveis
  setor?: Setor; // Compatibilidade antiga
  setores?: Setor[]; // Novo: múltiplos setores
  processo?: Processo;
  relacionada?: Demanda;
  anexos?: Anexo[];
  observadores?: ObservadorData[];
  atribuidas?: DemandaAtribuida[];
  demanda_autorizacao?: DemandaAutorizacao[]; // Autorizações pendentes
}

// Interfaces para relacionamentos
export interface DemandaAtribuida {
  id?: number;
  demanda_fk: number;
  usuario_fk: number;
  status: 'RECEBIDA' | 'EMPROGRESSO' | 'RECUSADA';
  active: boolean;
  created_at?: string;
  updated_at?: string;
  user?: User;
}

export interface DemandaAutorizacao {
  id: number;
  demanda_fk: number;
  autorizador_fk: number;
  usuario_fk: number | null; // ✅ NULL até aprovar/reprovar (snapshot só após ação)
  processo_fk: number;
  status: 'AGUARDANDO' | 'APROVADA' | 'REPROVADA';
  comentario?: string; // Justificativa da aprovação/reprovação
  created_at?: string;
  updated_at?: string;
  usuario?: User; // Usuario que autorizou (após aprovação/reprovação)
  autorizador?: Autorizador; // ✅ Autorizador atual (referência dinâmica com usuario_fk)
}

export interface Observador {
  id?: number;
  demanda_fk: number;
  observador_fk: number;
  active: boolean;
  created_at?: string;
  updated_at?: string;
  observador?: User;
}

// Tipo para representar observadores que podem vir do backend em formatos diferentes
export interface ObservadorPivot {
  demanda_fk: number;
  observador_fk: number;
  created_at?: string;
  updated_at?: string;
  active?: boolean;
}

export type ObservadorData =
  | Observador
  | (User & { pivot?: ObservadorPivot });

export interface CreateDemandaRequest {
  processo_fk: number[]; // Array de IDs dos processos
  demandante_fk: number;
  relacionada_fk?: number;
  titulo: string;
  prioridade: string;
  status?: 'EMATRIBUICAO' | 'RECEBIDA' | 'ATRIBUIDA' | 'EMPROGRESSO' | 'AGUARDANDOAUTORIZACAO' | 'SOLUCIONADA' | 'FINALIZADA' | 'DEVOLVIDA' | 'ERRADA' | 'RECUSADA' | 'EXCLUIDA';
  descricao: string;
  sugestao_entrega: string;
  observadores?: number[];
  anexos?: File[]; // Array de arquivos para upload
  template_respostas?: string; // JSON string com respostas de template
}

// Interface para Notificações (sistema de email do backend)
export interface Notificacao {
  id: number;
  titulo: string;
  comentario?: string;
  demanda_fk: number;
  recebedor_fk: number;
  editor_fk: number;
  visualizado: boolean;
  created_at: string;
  updated_at: string;
  
  // Relacionamentos
  demanda?: Demanda;
  editor?: User;
  recebedor?: User;
}

// Interface para Feriados
export interface Feriado {
  id: number;
  data: string; // ISO date format (YYYY-MM-DD)
  descricao: string;
  tipo: 'NACIONAL' | 'ESTADUAL' | 'MUNICIPAL' | 'FOLGA';
  abrangencia_id?: number;
  // abrangencia_type?: string;
  created_at?: string;
  updated_at?: string;
}

// Interfaces para Sistemas (Inventário de TI)
export interface ModuloSistema {
  id: number;
  nome: string;
  descricao?: string;
  sistema_fk: number;
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface PerfilSistema {
  id: number;
  nome: string;
  descricao?: string;
  sistema_fk: number;
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface Sistema {
  id: number;
  nome: string;
  descricao: string;
  categoria: 'Software de Negócio' | 'Banco de Dados' | 'Sistema Operacional' | 'Web';
  versao_atual: string;
  proprietario_fk: number;
  gestor_tecnico_fk: number;
  status_ciclo_vida: string;
  fornecedor: string;
  tipo_licenca: string;
  quantidade_licencas: number;
  data_expiracao?: string;
  data_renovacao?: string;
  custo_anual: number;
  servidores_infraestrutura: string;
  servico_negocio_relacionado: string;
  documentacao_link?: string;
  suporte_dr: string;
  suporte_dn: string;
  suporte_fornecedor: string;
  active: boolean;
  created_at?: string;
  updated_at?: string;
  
  // Relacionamentos
  proprietario?: Setor;
  gestor_tecnico?: User;
  modulos?: ModuloSistema[];
  perfis?: PerfilSistema[];
}

export interface CreateSistemaRequest {
  nome: string;
  descricao: string;
  categoria: 'Software de Negócio' | 'Banco de Dados' | 'Sistema Operacional' | 'Web';
  versao_atual: string;
  proprietario_fk: number;
  gestor_tecnico_fk: number;
  status_ciclo_vida: string;
  fornecedor: string;
  tipo_licenca: string;
  quantidade_licencas: number;
  data_expiracao?: string;
  data_renovacao?: string;
  custo_anual: number;
  servidores_infraestrutura: string;
  servico_negocio_relacionado: string;
  documentacao_link?: string;
  suporte_dr: string;
  suporte_dn: string;
  suporte_fornecedor: string;
  modulos?: Array<{
    id?: number;
    nome: string;
    descricao?: string;
  }>;
  perfis?: Array<{
    id?: number;
    nome: string;
    descricao?: string;
  }>;
}